<template>
	<div class="application-slide-wrapper">
		<v-card
			class="grid application-card"
			variant="flat"
			@click="showDetailDialog = true"
		>
			<div
				v-if="slideInfo.titleText"
				class="sgnl-paragraph-s-medium grid-title sgnl-text-color-default"
			>
				{{ slideInfo.titleText }}
			</div>
			<SkeletonBlock
				v-else
				height="var(--sgnl-unit-24)"
				radius="var(--sgnl-border-radius-6)"
				class="grid-title sgnl-text-color-default"
			/>
			<div class="grid-tags">
				<template v-if="repoData">
					<v-chip
						variant="outlined"
						v-for="(tag, i) in repoData.topics?.slice(0, 3)"
						:key="i"
					>
						{{ tag }}
					</v-chip>
				</template>
				<template v-else>
					<SkeletonBlock
						width="100px"
						height="var(--sgnl-unit-24)"
						radius="var(--sgnl-border-radius-6)"
					/>
					<SkeletonBlock
						width="100px"
						height="var(--sgnl-unit-24)"
						radius="var(--sgnl-border-radius-6)"
					/>
					<SkeletonBlock
						width="100px"
						height="var(--sgnl-unit-24)"
						radius="var(--sgnl-border-radius-6)"
					/>
				</template>
			</div>
			<div class="grid-actions">
				<TooltipButton
					variant="outlined"
					width="auto"
					:disabled="!slideInfo.applicationURL"
					target="_blank"
					:href="slideInfo.applicationURL"
				>
					<template #btnContent>
						<component
							v-if="applicationHostIcon"
							:is="applicationHostIcon"
						/>
						<span>Open</span>
					</template>
					<template #tooltipContent> Open application on {{ applicationURL.hostname }} </template>
				</TooltipButton>
				<TooltipButton
					variant="flat"
					color="primary"
					width="auto"
					:disabled="!slideInfo.applicationURL || isConnected"
					:tooltipText="isConnected ? 'Already in your account' : 'Add to your account'"
					@click.stop="connectRepo"
				>
					<template #btnContent>
						<template v-if="isConnected">
							<Checkmark16 />
							<span>Connected</span>
						</template>
						<template v-else>
							<BookmarkAdd16 />
							<span>Connect</span>
						</template>
					</template>
				</TooltipButton>
			</div>
		</v-card>
		<ApplicationDetailDialog
			v-if="showDetailDialog"
			v-model:showDialog="showDetailDialog"
			:applicationInfo="slideInfo"
			:initialRepoData="repoData"
		/>
	</div>
</template>

<script lang="ts">
import { defineComponent, markRaw, PropType } from "vue";
import ApplicationDetailDialog from "@/components/Home/ApplicationDetailDialog.vue";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import { getRepoData } from "@/js/githubClient";
import { navigateToConnectRepo } from "@/js/repositoryUtilities";
import { useRepositoriesStore } from "@/stores/repositories";
import type { Endpoints } from "@octokit/types";

import { ApplicationSlideInfo } from "@/types/home";
import LogoGithub16 from "@carbon/icons-vue/es/logo--github/16";
import BookmarkAdd16 from "@carbon/icons-vue/es/bookmark--add/16";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";
import Launch16 from "@carbon/icons-vue/es/launch/16";

export default defineComponent({
	name: "ApplicationSlide",
	components: {
		TooltipButton,
		ApplicationDetailDialog,
		SkeletonBlock,
		LogoGithub16,
		BookmarkAdd16,
		Checkmark16,
		Launch16,
	},
	props: {
		slideInfo: { type: Object as PropType<ApplicationSlideInfo>, required: true },
	},
	setup() {
		const repositoriesStore = useRepositoriesStore();
		return { repositoriesStore };
	},
	data: (): {
		showDetailDialog: boolean;
		repoData: undefined | Endpoints["GET /repos/{owner}/{repo}"]["response"]["data"];
		loading: boolean;
	} => ({
		showDetailDialog: false,
		repoData: undefined,
		loading: false,
	}),
	computed: {
		applicationURL(): URL {
			return new URL(this.slideInfo.applicationURL);
		},
		applicationHostIcon() {
			switch (this.applicationURL.hostname) {
				case "github.com":
					return markRaw(LogoGithub16);
			}
			return null;
		},
		demoVideoURL(): URL | undefined {
			return this.slideInfo.demoVideoURL
				? new URL(this.slideInfo.demoVideoURL, import.meta.env.VITE_ASSET_SERVER_URL)
				: undefined;
		},
		isConnected(): boolean {
			return this.repositoriesStore.isURLConnected(this.applicationURL.href);
		},
	},
	methods: {
		async getRepoData() {
			this.loading = true;
			try {
				const resp = await getRepoData(this.applicationURL.pathname.slice(1));
				this.repoData = resp.data;
			} catch {
				// monitoringCaptureError(error, "Get application repository details");
			} finally {
				this.loading = false;
			}
		},
		connectRepo() {
			navigateToConnectRepo(this.$router, this.applicationURL);
		},
	},
	watch: {
		// Watch for changes in the entire applicationInfo object
		slideInfo: {
			handler() {
				this.getRepoData();
			},
			deep: true,
			immediate: false,
		},
	},
	mounted() {
		this.getRepoData();
	},
});
</script>

<style lang="scss" scoped>
.application-slide-wrapper {
	height: 100%;
}
.application-card {
	padding: var(--sgnl-space-12);
	background: transparent;
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	border-radius: var(--sgnl-border-radius-6);
	height: 100%;
	min-height: var(--sgnl-unit-192);
	transition:
		transform var(--sgnl-motion-duration-fast-02) var(--sgnl-productive-effect-standard),
		border-color var(--sgnl-motion-duration-moderate-01) var(--sgnl-productive-effect-standard);
}
.application-card:hover {
	border-color: var(--sgnl-default-border-hover);
}
.application-card:active {
	transform: scale(0.99);
}
.grid {
	display: grid;
	gap: var(--sgnl-space-16);
	grid-template-rows: auto 1fr auto;
	grid-template-areas:
		"title"
		"tags"
		"actions";
}

.grid-title {
	grid-area: title;

	display: -webkit-box;
	-webkit-line-clamp: 2;
	line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	min-height: calc(var(--sgnl-font-line-height-paragraph-s) * 2);
}
.grid-tags {
	grid-area: tags;
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
	min-height: calc(var(--sgnl-unit-24) * 2 + 0.5rem);
	align-content: flex-start;
}

.grid-tags :deep(.v-chip) {
	font-family: var(--sgnl-font-family-mono);
	font-size: var(--sgnl-font-size-caption);
}
.grid-actions {
	grid-area: actions;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

@media (min-width: map-get($grid-breakpoints, lg)) {
	.grid-actions {
		flex-direction: row;
		gap: 0.75rem;
	}

	.grid-actions :deep(.v-btn) {
		flex: 1 1 0;
	}
}

.grid-actions :deep(.v-btn svg) {
	width: var(--sgnl-icon-16);
	height: var(--sgnl-icon-16);
	flex-shrink: 0;
}
</style>
